/**
 * MED-1346: the words for 1:1 no-shows and late changes. Medley pays the
 * coach for at most two missed sessions on each 1:1.
 */

import type { LatestMiss, MissClaim, MissClaimKind, OneOnOneMiss } from '../../types';

export const MISS_PAY_CAP = 2;

export const NO_SHOW_DEFINITION =
  'No show means the participant did not attend the 1:1 and gave no prior notice.';

const KIND_LABELS: Record<MissClaimKind, string> = {
  no_show: 'No show',
  late_cancel: 'Late cancellation',
  late_reschedule: 'Late reschedule',
};

const NOT_PAID_TEXT =
  'Medley will not pay for this session. Medley pays for two missed sessions on each 1:1 at most.';

/**
 * The confirm text before a no-show saves. paidMissCount is the number of
 * paid misses on this 1:1 before this one; null when it did not load.
 */
export function noShowConfirmText(
  paidMissCount: number | null,
  participantName: string
): string {
  if (paidMissCount === null) {
    return 'Medley pays you for the first two missed sessions on each 1:1.';
  }
  if (paidMissCount >= MISS_PAY_CAP) return NOT_PAID_TEXT;
  if (paidMissCount === 1) return 'Medley will pay you for this session.';
  return `Medley will pay you for this session. Please try to reschedule with ${participantName}.`;
}

/** F11: the result after a no-show saves, from the claim the server wrote. */
export function noShowResultText(claim: MissClaim): string {
  if (claim.decision !== 'paid') return NOT_PAID_TEXT;
  // The first paid miss asks for a reschedule; the second gives the payment line only.
  return noShowConfirmText(claim.paidMissNumber === 1 ? 0 : 1, claim.participantName);
}

/**
 * "4 hours 45 minutes before it started", "45 minutes before it started",
 * "after it started". Minutes round down, so a late change never reads as 24 hours.
 */
export function noticeText(hoursNotice: number): string {
  if (hoursNotice <= 0) return 'after it started';
  const totalMinutes = Math.floor(hoursNotice * 60);
  if (totalMinutes < 1) return 'less than 1 minute before it started';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [
    hours > 0 ? `${hours} hour${hours === 1 ? '' : 's'}` : null,
    minutes > 0 ? `${minutes} minute${minutes === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return `${parts.join(' ')} before it started`;
}

/**
 * "Ana Lee's 1:1 on Tue, Oct 7, 2:00 PM ET was canceled 5 hours before it started."
 * It does not say who made the change: staff, the coach or the participant can.
 */
export function missChangeText(claim: MissClaim, sessionLabel: string): string {
  const verb = claim.kind === 'late_reschedule' ? 'rescheduled' : 'canceled';
  return `${claim.participantName}'s ${sessionLabel} was ${verb} ${noticeText(claim.hoursNotice)}.`;
}

/** The question the popup and the dashboard card both ask. */
export const MISS_QUESTION = "Did you have less than 24 hours' notice?";

/** N2: staff answer for the coach, so their question names the coach. */
export const MISS_QUESTION_STAFF = "Did the coach have less than 24 hours' notice?";

/** The ticket question, with the change first. */
export function missQuestionText(
  claim: MissClaim,
  sessionLabel: string,
  asStaff = false
): string {
  return `${missChangeText(claim, sessionLabel)} ${asStaff ? MISS_QUESTION_STAFF : MISS_QUESTION}`;
}

/** The message after an answer, from the answered claim. Null means no message. */
export function missAnswerText(claim: MissClaim): string | null {
  if (claim.decision === 'paid')
    return 'Medley will issue a payment for this session.';
  if (claim.decision === 'unpaid_cap') return NOT_PAID_TEXT;
  if (claim.decision === 'declined' && claim.kind !== 'late_reschedule') {
    return `Please try to reschedule with ${claim.participantName}.`;
  }
  return null;
}

/**
 * F12: a change saves, then asks about the notice only for a pending claim
 * made before the start. A change after the start never asks.
 */
export function asksLateChangeQuestion(claim: MissClaim | null | undefined): claim is MissClaim {
  return !!claim && claim.decision === 'pending' && claim.hoursNotice > 0;
}

/** Paid, capped and pending claims win the label over on-time and declined ones. */
const LABEL_DECISIONS = new Set(['paid', 'unpaid_cap', 'pending']);

/**
 * The claim a 1:1 shows as its label, the same rule as the API latestMiss:
 * the newest paid, capped or pending claim, else the newest of the rest.
 * `claims` is newest first.
 */
export function labelClaim<T extends Pick<LatestMiss, 'decision'>>(
  claims: T[] | null | undefined
): T | null {
  if (!claims || claims.length === 0) return null;
  return claims.find(c => LABEL_DECISIONS.has(c.decision)) ?? claims[0];
}

/**
 * The label a 1:1 shows for its label claim. A change with 24 hours' notice
 * or more shows "Rescheduled" for a reschedule and nothing for a cancel.
 * scheduledStart is the 1:1's start now. A no-show or late cancel undates
 * the 1:1, so a different start means it was dated again, and the label
 * reads as history ("Earlier no show").
 */
export function missLabel(
  miss: Pick<LatestMiss, 'kind' | 'decision' | 'originalStart' | 'noShowCount'> | null | undefined,
  scheduledStart?: string | null
): { label: string; color: string; description: string } | null {
  if (!miss) return null;
  if (miss.decision === 'pending') {
    return {
      label: 'Pending',
      color: 'warningAmber',
      description: 'A late change waits for the coach to confirm the notice.',
    };
  }
  if (miss.decision === 'on_time' || miss.decision === 'declined') {
    return miss.kind === 'late_reschedule'
      ? {
          label: 'Rescheduled',
          color: 'gray',
          description: "Rescheduled with 24 hours' notice or more.",
        }
      : null;
  }
  const datedAgain =
    miss.kind !== 'late_reschedule' &&
    !!scheduledStart &&
    new Date(scheduledStart).getTime() !== new Date(miss.originalStart).getTime();
  return {
    label: datedAgain
      ? miss.kind === 'no_show' && (miss.noShowCount ?? 1) > 1
        ? 'Earlier no shows'
        : `Earlier ${KIND_LABELS[miss.kind].toLowerCase()}`
      : KIND_LABELS[miss.kind],
    color: miss.kind === 'no_show' ? 'dangerRed' : 'warningAmber',
    description:
      miss.decision === 'paid'
        ? 'Medley pays the coach for this missed session.'
        : 'Not paid: this 1:1 already has two paid missed sessions.',
  };
}

export function missKindLabel(kind: MissClaimKind): string {
  return KIND_LABELS[kind];
}

/** "No show - paid", "Late reschedule - unpaid". */
export function missEntryText(miss: OneOnOneMiss): string {
  return `${KIND_LABELS[miss.kind]} - ${miss.result}`;
}

/** "2 · No show - paid, Late reschedule - unpaid", or empty for no misses. */
export function missesCellText(misses: OneOnOneMiss[]): string {
  return misses.length === 0 ? '' : `${misses.length} · ${misses.map(missEntryText).join(', ')}`;
}

/** The Data Library "1:1 history" tab for one participant. */
export function oneOnOneHistoryHref(cohortId: string, participantId: string): string {
  const params = new URLSearchParams({ cohort: cohortId, tab: 'one_on_one_history', participant: participantId });
  return `/data-library?${params.toString()}`;
}
